import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { friendlyMessage } from '@/api/http'
import { projectApi } from '@/api/projectApi'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group'
import { CASCADE_STOP_STAGES, cascadeStopStageLabelKey } from '@/lib/cascadePolicy'
import type { CascadeAutoThreshold, CascadePolicy, CascadeStopAt, UpdateCascadePolicyInput } from '@/types/pulse'
import { formatCount } from '@/utils/format'

const cascadeThresholds: CascadeAutoThreshold[] = ['passed_only', 'review_ok', 'manual']
export const cascadePolicyQueryKey = (projectId: string) => ['project-cascade-policy', projectId] as const

export function buildCascadePolicyUpdate(
  policy: CascadePolicy,
  change: Partial<Pick<CascadePolicy, 'cascade_auto_threshold' | 'cascade_stop_at'>>,
): UpdateCascadePolicyInput {
  return {
    cascade_auto_threshold: change.cascade_auto_threshold ?? policy.cascade_auto_threshold,
    cascade_stop_at: change.cascade_stop_at ?? policy.cascade_stop_at,
  }
}

// Each simple preset is a complete policy update, including the endpoint.
const cascadePresets = {
  text: { cascade_auto_threshold: 'passed_only', cascade_stop_at: 'text' },
  storyboard: { cascade_auto_threshold: 'passed_only', cascade_stop_at: 'storyboard' },
  pages: { cascade_auto_threshold: 'passed_only', cascade_stop_at: 'image' },
} satisfies Record<string, UpdateCascadePolicyInput>

export function CascadePolicySettings({ projectId, simple = false }: { projectId: string; simple?: boolean }) {
  const { t, i18n } = useTranslation()
  const queryClient = useQueryClient()
  const query = useQuery({
    queryKey: cascadePolicyQueryKey(projectId),
    queryFn: () => projectApi.getCascadePolicy(projectId),
    enabled: Boolean(projectId),
  })
  const mutation = useMutation({
    mutationFn: (change: Partial<Pick<CascadePolicy, 'cascade_auto_threshold' | 'cascade_stop_at'>>) => {
      const current = query.data ?? {
        cascade_auto_threshold: 'passed_only' as const,
        cascade_stop_at: 'text' as const,
        cascade_config_explicit: false,
      }
      return projectApi.putCascadePolicy(projectId, buildCascadePolicyUpdate(current, change))
    },
    onSuccess: (policy) => {
      queryClient.setQueryData(cascadePolicyQueryKey(projectId), policy)
      toast.success(t('detail.cascadePolicy.saved'))
    },
    onError: (error) => {
      toast.error(friendlyMessage(error, t('detail.cascadePolicy.saveFailed')))
    },
  })

  const policy = query.data
  const threshold = policy?.cascade_auto_threshold ?? 'passed_only'
  const stopAt = policy?.cascade_stop_at ?? 'text'
  const estimate = useQuery({
    queryKey: ['project-cascade-estimate', projectId, stopAt],
    queryFn: () => projectApi.getCascadeEstimate(projectId, stopAt),
    enabled: Boolean(projectId && policy),
  })
  const tokens = estimate.data?.estimated_tokens
  const estimateText = estimate.isError ? t('cascadeEstimate.failed') : tokens == null ? t('cascadeEstimate.noData') : t('project.wizard.cascadeCostEstimate', {
    station: t(cascadeStopStageLabelKey(stopAt)),
    tokens: formatCount(tokens, i18n.resolvedLanguage ?? i18n.language),
  })
  const disabled = !policy || query.isError || mutation.isPending

  if (simple) {
    const selected = Object.entries(cascadePresets).find(([, preset]) =>
      preset.cascade_auto_threshold === threshold && preset.cascade_stop_at === stopAt)?.[0] ?? ''
    return (
      <div>
        <div className="mb-2 flex items-center justify-between gap-3">
          <p className="text-xs font-medium text-muted-foreground">{t('detail.cascadePolicy.simpleTitle')}</p>
          {mutation.isPending ? <Loader2 className="size-4 animate-spin text-muted-foreground" /> : null}
        </div>
        <ToggleGroup
          className="grid w-full grid-cols-1 sm:grid-cols-3"
          type="single"
          value={selected}
          disabled={disabled}
          onValueChange={(value) => {
            const preset = cascadePresets[value as keyof typeof cascadePresets]
            if (preset) mutation.mutate(preset)
          }}
          aria-label={t('detail.cascadePolicy.simpleTitle')}
        >
          {Object.keys(cascadePresets).map((key) => (
            <ToggleGroupItem key={key} value={key}>{t(`detail.cascadePolicy.presets.${key}`)}</ToggleGroupItem>
          ))}
        </ToggleGroup>
        {!selected && policy ? <p className="mt-2 text-xs text-muted-foreground">{t('detail.cascadePolicy.customPreset')}</p> : null}
        {selected === 'pages' ? <p className="mt-2 text-xs text-muted-foreground">{t('detail.cascadePolicy.pagesHint')}</p> : null}
        <p className="mt-2 text-xs text-muted-foreground">{estimateText}</p>
      </div>
    )
  }

  return (
    <div>
      <div className="mb-2 flex items-center justify-between gap-3">
        <p className="text-xs font-medium uppercase text-muted-foreground">{t('detail.cascadePolicy.title')}</p>
        {disabled ? <Loader2 className="size-4 animate-spin text-muted-foreground" /> : null}
      </div>
      <p className="mb-3 text-xs text-muted-foreground">{t('detail.cascadePolicy.description')}</p>
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-2">
          <Label>{t('project.wizard.cascadeThreshold')}</Label>
          <Select
            value={threshold}
            disabled={disabled}
            onValueChange={(value) => mutation.mutate({ cascade_auto_threshold: value as CascadeAutoThreshold })}
          >
            <SelectTrigger className="w-full"><SelectValue /></SelectTrigger>
            <SelectContent>
              {cascadeThresholds.map((value) => (
                <SelectItem key={value} value={value}>{t(`project.wizard.cascadeThresholds.${value}`)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>{t('project.wizard.cascadeStopAt')}</Label>
          <Select
            value={stopAt}
            disabled={disabled}
            onValueChange={(value) => mutation.mutate({ cascade_stop_at: value as CascadeStopAt })}
          >
            <SelectTrigger className="w-full"><SelectValue /></SelectTrigger>
            <SelectContent>
              {CASCADE_STOP_STAGES.map((stage) => (
                <SelectItem key={stage} value={stage}>{t(cascadeStopStageLabelKey(stage))}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="mt-3 rounded-md border border-[color-mix(in_oklch,var(--nf-st-review)_40%,transparent)] bg-[color-mix(in_oklch,var(--nf-st-review)_10%,transparent)] p-3 text-sm">
        <p className="font-medium">{t('project.wizard.cascadeCostTitle')}</p>
        <p className="mt-1 text-muted-foreground">
          {estimateText}
        </p>
      </div>
    </div>
  )
}
